import React, { useEffect, useState } from 'react';
import { DrSebiHeroSection } from '../components/DrSebiHeroSection';
import { DrSebiReasonsSection } from '../components/DrSebiReasonsSection';
import { DrSebiIngredientsSection } from '../components/DrSebiIngredientsSection';
import { DrSebiCTASection } from '../components/DrSebiCTASection';
import { BodyReliefSolutionSection } from '../components/BodyReliefSolutionSection';
import { BodyReliefMechanismSection } from '../components/BodyReliefMechanismSection';
import { BodyReliefBenefitsSection } from '../components/BodyReliefBenefitsSection';
import { BodyReliefTrustSection } from '../components/BodyReliefTrustSection';
import { PolicyModal } from '../components/PolicyModal';
import { SalesNotification } from '../components/SalesNotification';
import { PageTracker } from '../components/PageTracker';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
type PolicyType = 'privacy' | 'terms' | 'refund';
export function DrSebiAdvertorialPage() {
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [showSalesNotification, setShowSalesNotification] = useState(false);
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  useEffect(() => {
    // Set page title
    document.title = '10 Reasons Why Dr. Sebi Taught That Ginger-Based Botanicals Restore Joint Flow | Ginext';
    // Scroll to top on mount
    window.scrollTo(0, 0);
    // Handle floating CTA and sales notification visibility
    const handleScroll = () => {
      const scrollPercentage = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      // Show floating CTA at 25%
      setShowFloatingCTA(scrollPercentage >= 25);
      // Show sales notification at 30%
      setShowSalesNotification(scrollPercentage >= 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  return <main className="min-h-screen bg-white font-sans selection:bg-ginger/20 selection:text-charcoal" data-id="element-1716">
      <PageTracker pageName="dr_sebi_advertorial" data-id="element-1717" />

      {/* Editorial Header */}
      <DrSebiHeroSection data-id="element-1718" />

      {/* Main Content */}
      <DrSebiReasonsSection data-id="element-1719" />

      {/* Ingredients Breakdown */}
      <DrSebiIngredientsSection data-id="element-1720" />

      {/* First Pricing Section */}
      <div id="pricing-section" data-id="element-1721">
        <DrSebiCTASection data-id="element-1722" />
      </div>

      {/* Additional Sections from /ginext */}
      <BodyReliefSolutionSection data-id="element-1723" />
      <BodyReliefMechanismSection data-id="element-1724" />
      <BodyReliefBenefitsSection data-id="element-1725" />
      <BodyReliefTrustSection data-id="element-1726" />

      {/* Second Pricing Section */}
      <DrSebiCTASection data-id="element-1727" />

      {/* Floating CTA Button */}
      <AnimatePresence data-id="element-1728">
        {showFloatingCTA && <motion.button initial={{
        opacity: 0,
        y: 100
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 100
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.99
      }} onClick={scrollToPricing} className="fixed bottom-0 left-0 right-0 sm:bottom-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:max-w-md z-50 bg-ginger hover:bg-ginger/95 text-white px-4 py-2.5 sm:rounded-lg shadow-[0_-4px_20px_rgb(0,0,0,0.1)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_-8px_30px_rgb(0,0,0,0.15)] sm:hover:shadow-[0_12px_40px_rgb(0,0,0,0.16)] backdrop-blur-sm border-t sm:border border-white/10 transition-all duration-300 flex items-center justify-between gap-3 group" data-id="element-1729">
            <div className="flex flex-col items-start gap-0.5" data-id="element-1730">
              <span className="font-medium text-xs sm:text-sm leading-none tracking-normal" data-id="element-1731">
                Save $30 – Try Risk Free
              </span>
              <span className="text-[10px] font-normal text-white/75 leading-none" data-id="element-1732">
                Free Shipping Included
              </span>
            </div>
            <div className="w-6 h-6 rounded-md bg-white/15 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/25 transition-colors flex-shrink-0" data-id="element-1733">
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-1734" />
            </div>
          </motion.button>}
      </AnimatePresence>

      {/* Sales Notification - Shows at 30% scroll */}
      {showSalesNotification && <SalesNotification data-id="element-1735" />}

      {/* Footer - Same as /ginext */}
      <footer className="bg-charcoal text-gray-400 py-4 text-center text-xs border-t border-white/10" data-id="element-1736">
        <div className="max-w-6xl mx-auto px-4" data-id="element-1737">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-1738">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-1739">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-1740">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-1741">
              Refund Policy
            </button>
          </div>
          <p data-id="element-1742">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-1743">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-1744" />
    </main>;
}